import React, { useEffect, useMemo, useState } from "react";
import "../styles/billing.css";
import { PLANS, getPlanByLimit, getEffectivePlanLimit } from "../lib/billingUtils";
import { useTranslation } from 'react-i18next';
import { getBillingInfo, initiatePayment } from "../api/billing";

const Billing = () => {
  const { t } = useTranslation();
  const [billingInfo, setBillingInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const [selectedPlanLimit, setSelectedPlanLimit] = useState(null);

  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        const data = await getBillingInfo();
        setBillingInfo(data);

        const company = data?.company;

        if (company?.payment_status === "trial") {
          setSelectedPlanLimit(null); // нічого не обрано на тріалі
        } else {
          const effectiveLimit = getEffectivePlanLimit(
            company?.active_boards_in_plan,
            company?.payment_status
          );
          setSelectedPlanLimit(effectiveLimit);
        }

      } catch (err) {
        console.error("Помилка завантаження білінгу:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBillingData();
  }, []);

  const company = billingInfo?.company;
  const payments = billingInfo?.payments || [];
  const paymentStatus = company?.payment_status;
  const daysLeftInTrial = company?.days_left_in_trial;
  const activeBoardsInPlan = company?.active_boards_in_plan;
  const activeBoardCount = company?.active_board_count;

  const effectivePlanLimit = useMemo(
    () => getEffectivePlanLimit(activeBoardsInPlan, paymentStatus),
    [activeBoardsInPlan, paymentStatus]
  );

  const currentPlan = useMemo(
    () => getPlanByLimit(effectivePlanLimit),
    [effectivePlanLimit]
  );

  const selectedPlan = useMemo(() => {
    if (selectedPlanLimit == null) return null;
    return getPlanByLimit(selectedPlanLimit);
  }, [selectedPlanLimit]);


  const statusLine = useMemo(() => {
    if (!paymentStatus) return null;

    if (paymentStatus === "trial") {
      return (
        <p className="status" dangerouslySetInnerHTML={{ __html: t('billing.trial_active', { days: daysLeftInTrial, boards: activeBoardCount }) }} />
      );
    }

    if (paymentStatus === "trial_expired") {
      return (
        <p className="status">{t('billing.trial_expired', { boards: activeBoardCount })}</p>
      );
    }

    if (paymentStatus === "paid") {
      return (
        <p className="status" dangerouslySetInnerHTML={{ __html: t('billing.subscription_active', { plan: `${currentPlan.title} (${t(currentPlan.descriptionKey)})`, boards: activeBoardCount }) }} />
      );
    }

    if (paymentStatus === "payment_expired") {
      return (
        <p className="status" dangerouslySetInnerHTML={{ __html: t('billing.subscription_expired', { plan: `${currentPlan.title} (${t(currentPlan.descriptionKey)})`, boards: activeBoardCount }) }} />
      );
    }

    return null;
  }, [paymentStatus, daysLeftInTrial, activeBoardCount, currentPlan, t]);

  const canPayNow = selectedPlanLimit != null;

  const handlePay = async () => {
    if (selectedPlanLimit == null) return;
    try {
      const res = await initiatePayment(company?.id, selectedPlanLimit);
      const html = await res.text();
      document.open();
      document.write(html);
      document.close();
    } catch (err) {
      console.error("Помилка оплати:", err);
      alert(err.message || t('billing.pay_error'));
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
  };

  // ✅ тепер early returns ПІСЛЯ усіх хуків
  if (loading) return <p className="loader-p">{t('billing.loading')}</p>;
  if (!billingInfo) return <p className="loader-p">{t('billing.load_error')}</p>;

  return (
    <div className="billing-container">
      <h2 className="page-title">{t('billing.title')}</h2>

      {statusLine}
        <h2 className="section-title">
          {t('billing.choose_plan')}
        </h2>
        <div className="tariffList">
            {PLANS.map((plan) => {
              const isSelected = selectedPlanLimit === plan.limit;
              const isCurrent = paymentStatus !== "trial" && paymentStatus != null && effectivePlanLimit === plan.limit;
              const buttonLabel = isCurrent ? t('billing.current_plan') : isSelected ? t('billing.selected') : t('billing.select_plan');

              return (
                <div
                  key={plan.limit}
                  className={`tariffCard ${isSelected ? "tariffCard--selected" : ""}`}
                >
                  <div className="tariffName">{plan.title}</div>
                  <div className="tariffBoard">{t(plan.descriptionKey)}</div>

                  <div className="tariffPrice">
                    <sup>USD</sup><p>{plan.price}</p><sup>{t('billing.per_month')}</sup>
                  </div>

                  <button
                    type="button"
                    className={`tariffButton ${isSelected ? "is-selected" : ""}`}
                    onClick={() => setSelectedPlanLimit(plan.limit)}
                  >
                    {buttonLabel}
                  </button>
                </div>
              );
            })}
        </div>
        <div className="billing-card">
          <div className="billing-change-plan">
            {selectedPlan && (
              <p className="billing-summary">
                <strong>{`$${selectedPlan.price}${t('billing.per_month')}`}</strong>
              </p>
            )}
            <button
              name="save-plan-btn"
              className="save-plan-btn button"
              onClick={handlePay}
              disabled={!canPayNow}
            >
              {t('billing.pay')}
            </button>
          </div>
        </div>

      {Array.isArray(payments) && payments.length > 0 && (
        <div className="billing-history">
          <h2 className="section-title">{t('billing.history_title')}</h2>

          <div className="payments-table-wrap">
            <table className="payments-table">
              <thead>
                <tr>
                  <th>{t('billing.col_date')}</th>
                  <th>{t('billing.col_amount')}</th>
                  <th>{t('billing.col_status')}</th>
                  <th>{t('billing.col_plan')}</th>
                  <th>{t('billing.col_order')}</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p, idx) => (
                  <tr key={p.order_reference || idx}>
                    <td>{formatDate(p.created_at || p.transaction_time)}</td>
                    <td>{p.amount} {p.currency}</td>
                    <td>{p.payment_status}</td>
                    <td>
                      {p.boards_in_plan
                        ? (() => {
                            const plan = getPlanByLimit(p.boards_in_plan);
                            return `${plan.title} (${t(plan.descriptionKey)})`;
                          })()
                        : "-"}
                    </td>
                    <td>
                      {p.order_reference}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
